import { useTonAddress } from "@tonconnect/ui-react";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
  useState,
} from "react";

import { gql, useMutation } from "@apollo/client";
import { useTelegram, useTelegramCloudStorage } from "../hooks/telegram";
import { showNotification } from "../lib/notification";
import config, { LEVELS } from "../miniapp.config";
import FullPageLoader from "../containers/FullPageLoader";

interface ClickerContextType {
  balance: number;
  level: string;
  tapEnergy: number;
  hourlyBalance: number;
}

interface CheckInContextType {
  streak: number;
}

interface TapContextType {
  tapsUsed: number;
  maxTaps: number;
  lastTapTime: number;
  pendingTaps: Array<{ timestamp: number; expiresAt: number }>;
}

interface WalletContextType {
  tonAddress?: string;
  isWalletConnected: boolean;
}

interface CurrentUserContextType {
  clicker: ClickerContextType;
  checkIn: CheckInContextType;
  tap: TapContextType;
  wallet: WalletContextType;
  isLoading: boolean;
  setBalance: (balance: number) => void;
  incrementTapsUsed: () => void;
  addBalance: (amount: number) => void;
  reduceBalance: (amount: number) => void;
  setTonAddress: (address: string) => void;
  showJoinBonus: boolean;
}

const initialState: CurrentUserContextType = {
  clicker: {
    balance: 0,
    level: LEVELS.BEGINNER,
    tapEnergy: 0,
    hourlyBalance: 0,
  },
  checkIn: { streak: 1 },
  tap: {
    tapsUsed: 0,
    maxTaps: 500,
    lastTapTime: 0,
    pendingTaps: [],
  },
  wallet: {
    isWalletConnected: false,
  },
  isLoading: true,
  showJoinBonus: false,
  setBalance: () => {},
  incrementTapsUsed: () => {},
  addBalance: () => {},
  reduceBalance: () => {},
  setTonAddress: () => {},
};

const CurrentUserContext = createContext<CurrentUserContextType>(initialState);

interface CurrentUserProviderProps {
  children: ReactNode;
}

function useBackendUser() {
  const tonAddress = useTonAddress(false);
  const { user, startParams } = useTelegram();
  const { setItem, getItem } = useTelegramCloudStorage();
  const [state, setState] =
    useState<
      Omit<
        CurrentUserContextType,
        "setBalance" | "incrementTapsUsed" | "setTonAddress"
      >
    >(initialState);
  const [initComplete, setInitComplete] = useState({
    telegram: false,
    clicker: false,
  });

  const [initClicker] = useMutation(
    gql`
      mutation InitClicker(
        $username: String!
        $firstName: String!
        $lastName: String
        $startParams: String
      ) {
        initClicker(
          username: $username
          firstName: $firstName
          lastName: $lastName
          startParams: $startParams
        ) {
          clicker {
            balance
            level
            tapEnergy
            hourlyBalance
          }
          checkIn {
            streak
          }
          status
        }
      }
    `,
    {
      variables: {
        username: user?.username || user?.id || "",
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        startParams,
      },
      onCompleted: (data) => {
        setState((prev) => ({
          ...prev,
          clicker: data.initClicker.clicker,
          checkIn: data.initClicker.checkIn,
        }));
        setInitComplete((prev) => ({ ...prev, clicker: true }));
        if (
          data.initClicker.status === "CREATED" ||
          data.initClicker.status === "CREATED_JOIN_BONUS" ||
          data.initClicker.status === "CREATED_AND_MODIFIED"
        ) {
          setState((prev) => ({ ...prev, showJoinBonus: true }));
        }
      },
    }
  );

  const [addBalanceMutation] = useMutation(
    gql`
      mutation AddBalance($amount: Int!) {
        addBalance(amount: $amount) {
          status
          newBalance
        }
      }
    `,
    {
      onCompleted: (data) => {
        if (data.addBalance.status === "SUCCESS") {
          setState((prev) => ({
            ...prev,
            clicker: {
              ...prev.clicker,
              balance: data.addBalance.newBalance,
            },
          }));
        }
      },
    }
  );

  const setBalance = useCallback((balance: number) => {
    setState((prev) => ({
      ...prev,
      clicker: { ...prev.clicker, balance },
    }));
  }, []);

  const addBalance = useCallback(
    (amount: number) => {
      addBalanceMutation({ variables: { amount } });
    },
    [addBalanceMutation]
  );

  const reduceBalance = useCallback((amount: number) => {
    setState((prev) => ({
      ...prev,
      clicker: { ...prev.clicker, balance: prev.clicker.balance - amount },
    }));
  }, []);

  const saveTapState = useCallback(
    (tapState: TapContextType) => {
      setItem("tap", JSON.stringify(tapState), (error) => {
        if (error) console.error("Error saving tap state:", error);
      });
    },
    [setItem]
  );

  const startRefillTimer = useCallback(
    (expiresAt: number) => {
      const timeLeft = expiresAt - Date.now();
      if (timeLeft <= 0) return;

      setTimeout(() => {
        setState((prev) => {
          const newState = {
            ...prev,
            tap: {
              ...prev.tap,
              tapsUsed: Math.max(0, prev.tap.tapsUsed - 1),
              pendingTaps: prev.tap.pendingTaps.slice(1),
            },
          };

          saveTapState(newState.tap);

          if (newState.tap.pendingTaps.length > 0) {
            startRefillTimer(newState.tap.pendingTaps[0].expiresAt);
          }

          return newState;
        });
      }, timeLeft);
    },
    [saveTapState]
  );

  const incrementTapsUsed = useCallback(() => {
    const now = Date.now();
    const expiresAt = now + config.TAP_REFILL_TIME;

    setState((prev) => {
      if (prev.tap.tapsUsed >= prev.tap.maxTaps) {
        showNotification(
          "You have reached the maximum number of taps. Please wait for the refill."
        );
        return prev;
      }

      const newPendingTaps = [
        ...prev.tap.pendingTaps,
        { timestamp: now, expiresAt },
      ].sort((a, b) => a.timestamp - b.timestamp);

      const newState = {
        ...prev,
        tap: {
          ...prev.tap,
          tapsUsed: prev.tap.tapsUsed + 1,
          lastTapTime: now,
          pendingTaps: newPendingTaps,
        },
        clicker: {
          ...prev.clicker,
          balance: prev.clicker.balance + prev.clicker.tapEnergy,
        },
      };

      saveTapState(newState.tap);

      if (newPendingTaps.length === 1) {
        startRefillTimer(expiresAt);
      }

      return newState;
    });
  }, [saveTapState, startRefillTimer]);

  const setTonAddress = useCallback((tonAddress: string) => {
    setState((prev) => ({
      ...prev,
      wallet: {
        ...prev.wallet,
        isWalletConnected: true,
        tonAddress,
      },
    }));
  }, []);

  useEffect(() => {
    let mounted = true;

    const initUsingTelegram = async () => {
      if (!mounted) return;

      try {
        getItem("tap", (error, result: string | undefined) => {
          if (error) {
            console.error("Error loading tap state:", error);
            return;
          }

          const now = Date.now();
          setState((prev) => {
            const baseState = {
              ...prev,
              wallet: {
                ...prev.wallet,
                isWalletConnected: !!tonAddress,
                tonAddress,
              },
            };

            if (!result) return baseState;

            const tap = JSON.parse(result) as TapContextType;
            const validPendingTaps = tap.pendingTaps
              .filter((t) => t.expiresAt > now)
              .sort((a, b) => a.timestamp - b.timestamp);

            if (validPendingTaps.length > 0) {
              startRefillTimer(validPendingTaps[0].expiresAt);
            }

            return {
              ...baseState,
              tap: {
                ...tap,
                tapsUsed: validPendingTaps.length,
                pendingTaps: validPendingTaps,
              },
            };
          });
          setInitComplete((prev) => ({ ...prev, telegram: true }));
        });
      } catch (error) {
        console.error("Error loading state:", error);
        setState((prev) => ({
          ...prev,
          wallet: {
            ...prev.wallet,
            isWalletConnected: !!tonAddress,
            tonAddress,
          },
        }));
        setInitComplete((prev) => ({ ...prev, telegram: true }));
      }
    };

    initUsingTelegram();
    return () => {
      mounted = false;
    };
  }, [tonAddress, getItem, saveTapState, startRefillTimer, initClicker]);

  useEffect(() => {
    if (initComplete.telegram) {
      initClicker({
        variables: {
          username: user?.username || "",
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          startParams: startParams,
        },
      });
    }
  }, [
    initClicker,
    initComplete.telegram,
    user?.username,
    user?.firstName,
    user?.lastName,
    startParams,
  ]);

  useEffect(() => {
    if (initComplete.telegram && initComplete.clicker) {
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }, 2000);
    }
  }, [initComplete]);

  return useMemo(
    () => ({
      ...state,
      setBalance,
      incrementTapsUsed,
      setTonAddress,
      addBalance,
      reduceBalance,
    }),
    [
      state,
      setBalance,
      incrementTapsUsed,
      setTonAddress,
      addBalance,
      reduceBalance,
    ]
  );
}

export function CurrentUserProvider({ children }: CurrentUserProviderProps) {
  const backendUser = useBackendUser();

  if (backendUser.isLoading) return <FullPageLoader />;

  return (
    <CurrentUserContext.Provider value={backendUser}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export default CurrentUserContext;
